import styled from "styled-components";
import css from "@styled-system/css";

import Icon from "../Icon/Icon";
import Typography from "../Typography/Typography";
import { ChangeEvent } from "react";

export interface PriceInputProps {
  /**
   * The value attribute of the input component
   */
  value?: number;
  /**
   * onChange callback
   */
  onChange: (value: number) => void;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   * Step value
   */
  step?: number;
  /**
   * Disable decrementing
   * */
  decrementDisabled?: boolean;
  /**
   * Disable incrementing
   */
  incrementDisabled?: boolean;
  /**
   * Enable the invalid state of the input component
   */
  invalidInput?: boolean;
  /**
   * Custom styling
   */
  css?: string;
  /**
   * Show currency besides the value
   */
  showCurrency?: boolean;
  /**
   * The min attribute of the input component
   */
  minimumInputValue?: number;
}

const StyledInputContainer = styled.div<Partial<PriceInputProps>>`
  display: flex;
  position: relative;

  border-radius: 8px;
  ${(props) =>
    css({
      width: "100%",
      height: "48px",
      backgroundColor: "background-default-color",
      boxShadow: "default",
      border: props.invalidInput ? "warningBorder" : "none",
    })}
  ${(props) => props.css}
`;

const StyledButton = styled.button`
  width: 48px;
  border: none;
  z-index: 1;
  padding: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  &[disabled] {
    opacity: 0.3;
  }
  :first-of-type {
    border-radius: 8px 0 0 8px;
  }
  :last-of-type {
    border-radius: 0 8px 8px 0;
  }
  :focus {
    outline: none;
  }
  ${css({
    backgroundColor: "background-default-color",
    color: "cta-tertiary-text-color-default",
    "&:focus": {
      border: "primaryBorder",
    },
  })}
`;

const StyledInput = styled.input<{ showCurrency?: boolean }>`
  border: none;
  border-radius: 0;
  flex: 1;
  min-width: 0;
  padding: 0;
  padding-left: 28px;
  margin: 0;
  text-align: left;
  box-shadow: none;
  border-left: 1px solid rgba(230, 235, 240, 0.78);
  border-right: 1px solid rgba(230, 235, 240, 0.78);

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    display: none;
  }
  -moz-appearance: textfield;
  ${(props) =>
    css({
      color: "grey-blue",
      lineHeight: 1,
      fontFamily: "normal",
      fontSize: "h6",
      fontWeight: "semiBold",
      paddingLeft: props.showCurrency ? 7 : 5,
      "&:focus": {
        outline: "none",
        border: "primaryBorder",
      },
    })}
`;

const Minus = styled.div`
  width: 18px;
  height: 3px;
  ${css({
    backgroundColor: "cta-tertiary-text-color-default",
  })}
`;

const ScreenReadersSpan = styled.span`
  :not(:focus),
  :not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

const CurrencySymbol = styled(Typography)`
  position: absolute;
  font-weight: 500;
  opacity: 0.5;
  top: -23px;
  left: 64px;
`;

export default function PriceInput({
  value = 100,
  step = 100,
  decrementDisabled = false,
  incrementDisabled = false,
  invalidInput = false,
  onChange,
  showCurrency = true,
  minimumInputValue = 100,
  ...rest
}: PriceInputProps) {
  const increment = () => {
    onChange(value + step);
  };

  const decrement = () => {
    onChange(value - step);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value ? parseInt(event.target.value) : 0);
  };

  const isMinValue = (value: number) => {
    return value <= 0;
  };

  return (
    <StyledInputContainer
      data-testid="price-input-container"
      invalidInput={invalidInput}
      {...rest}
    >
      <StyledButton
        onClick={decrement}
        disabled={isMinValue(value) || decrementDisabled}
        data-en="click"
        data-ec="button"
        data-el="pricing"
        data-ea="navigate"
        data-es=""
      >
        <Minus />
        <ScreenReadersSpan>-</ScreenReadersSpan>
      </StyledButton>
      {showCurrency && (
        <CurrencySymbol variant="h6" color="grey-blue">
          &pound;
        </CurrencySymbol>
      )}
      <StyledInput
        type="number"
        aria-label="bid price"
        value={value}
        min={minimumInputValue}
        step={step}
        onChange={handleChange}
        showCurrency={showCurrency}
      />
      <StyledButton
        onClick={increment}
        disabled={incrementDisabled}
        data-en="click"
        data-ec="button"
        data-el="pricing"
        data-ea="navigate"
        data-es=""
      >
        <Icon
          name="plus"
          color="primary-light-blue"
          width="24px"
          height="24px"
        />
        <ScreenReadersSpan>+</ScreenReadersSpan>
      </StyledButton>
    </StyledInputContainer>
  );
}
